import { useEffect, useRef, useSyncExternalStore } from "react";
import { NUDGE_GESTURE_MS, sameJson } from "./history";

const DRAFT_HISTORY_CAP = 50;

interface DraftEntry<T> {
  undo: T;
  redo: T;
}

export interface DraftHistory<T> {
  subscribe: (listener: () => void) => () => void;
  getSnapshot: () => { value: T; version: number };
  get: () => T;
  /** Apply `fn` immediately. One undo step, unless a gesture is open. */
  update: (fn: (current: T) => T) => void;
  begin: (opts?: { blockUndo?: boolean }) => void;
  end: () => void;
  cancel: () => void;
  /** Collapse a burst of updates into one step after a quiet gap. */
  coalesce: (fn: () => void) => void;
  undo: () => void;
  redo: () => void;
  canUndo: () => boolean;
  canRedo: () => boolean;
  dispose: () => void;
}

/**
 * In-memory undo stack for a draft that is not the room design (the
 * furniture creator). Same gesture rules as the design store: a drag is
 * one step, cancel restores the baseline, and a quiet burst of edits
 * collapses into one step.
 */
export function createDraftHistory<T>(
  initial: T,
  idleMs = NUDGE_GESTURE_MS
): DraftHistory<T> {
  let current = structuredClone(initial);
  let version = 0;
  let snapshot = { value: current, version };
  const listeners = new Set<() => void>();
  const undoStack: DraftEntry<T>[] = [];
  const redoStack: DraftEntry<T>[] = [];
  let depth = 0;
  let baseline: T | null = null;
  let blockUndo = false;
  let coalesceOwns = false;
  let coalesceTimer: ReturnType<typeof setTimeout> | null = null;

  function emit() {
    version += 1;
    snapshot = { value: current, version };
    for (const listener of listeners) listener();
  }

  function clearCoalesceTimer() {
    if (coalesceTimer != null) {
      clearTimeout(coalesceTimer);
      coalesceTimer = null;
    }
  }

  function pushUndo(entry: DraftEntry<T>) {
    undoStack.push(entry);
    if (undoStack.length > DRAFT_HISTORY_CAP) {
      undoStack.splice(0, undoStack.length - DRAFT_HISTORY_CAP);
    }
    redoStack.length = 0;
  }

  function closeGesture() {
    if (depth <= 0) return;
    depth -= 1;
    if (depth > 0) return;
    clearCoalesceTimer();
    coalesceOwns = false;
    const base = baseline;
    baseline = null;
    blockUndo = false;
    if (base && !sameJson(base, current)) {
      pushUndo({ undo: base, redo: structuredClone(current) });
    }
    emit();
  }

  function commitOpen() {
    while (depth > 0) closeGesture();
  }

  function apply(next: T) {
    if (sameJson(current, next)) return;
    if (depth > 0) {
      if (!baseline) baseline = structuredClone(current);
      current = structuredClone(next);
      emit();
      return;
    }
    const undo = structuredClone(current);
    current = structuredClone(next);
    pushUndo({ undo, redo: structuredClone(current) });
    emit();
  }

  return {
    subscribe: (listener) => {
      listeners.add(listener);
      return () => listeners.delete(listener);
    },
    getSnapshot: () => snapshot,
    get: () => current,
    update: (fn) => apply(fn(current)),
    begin: (opts) => {
      if (depth === 0) {
        baseline = structuredClone(current);
        blockUndo = Boolean(opts?.blockUndo);
      } else if (opts?.blockUndo) {
        blockUndo = true;
      }
      depth += 1;
    },
    end: () => closeGesture(),
    cancel: () => {
      if (depth <= 0) return;
      if (depth > 1) {
        depth -= 1;
        return;
      }
      const base = baseline;
      depth = 0;
      baseline = null;
      blockUndo = false;
      clearCoalesceTimer();
      coalesceOwns = false;
      if (base && !sameJson(base, current)) current = base;
      emit();
    },
    coalesce: (fn) => {
      if (depth === 0) {
        baseline = structuredClone(current);
        blockUndo = false;
        depth += 1;
        coalesceOwns = true;
      }
      fn();
      if (!coalesceOwns) return;
      clearCoalesceTimer();
      coalesceTimer = setTimeout(() => {
        coalesceTimer = null;
        closeGesture();
      }, idleMs);
    },
    undo: () => {
      if (depth > 0 && blockUndo) return;
      const reopen = depth > 0 && !coalesceOwns;
      commitOpen();
      const entry = undoStack.pop();
      if (!entry) {
        emit();
        return;
      }
      redoStack.push(entry);
      current = structuredClone(entry.undo);
      if (reopen) {
        baseline = structuredClone(current);
        blockUndo = false;
        depth += 1;
      }
      emit();
    },
    redo: () => {
      if (depth > 0 && blockUndo) return;
      const reopen = depth > 0 && !coalesceOwns;
      commitOpen();
      const entry = redoStack.pop();
      if (!entry) {
        emit();
        return;
      }
      undoStack.push(entry);
      if (undoStack.length > DRAFT_HISTORY_CAP) {
        undoStack.splice(0, undoStack.length - DRAFT_HISTORY_CAP);
      }
      current = structuredClone(entry.redo);
      if (reopen) {
        baseline = structuredClone(current);
        blockUndo = false;
        depth += 1;
      }
      emit();
    },
    canUndo: () =>
      undoStack.length > 0 || (depth > 0 && !blockUndo && baseline != null && !sameJson(baseline, current)),
    canRedo: () => redoStack.length > 0,
    dispose: () => {
      clearCoalesceTimer();
      coalesceOwns = false;
    },
  };
}

/** Subscribe a component to a draft history created once for its lifetime. */
export function useDraftHistory<T>(initial: T): { draft: T; history: DraftHistory<T> } {
  const ref = useRef<DraftHistory<T> | null>(null);
  if (!ref.current) ref.current = createDraftHistory(initial);
  const history = ref.current;
  const snap = useSyncExternalStore(history.subscribe, history.getSnapshot, history.getSnapshot);
  useEffect(() => () => history.dispose(), [history]);
  return { draft: snap.value, history };
}
